// NPM MODULES
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  InputLabel,
  Stack,
} from '@mui/material';

// COMPONENTS
import ListItem from './ContactRow';
import ContactEdit from './ContactEdit';
import ContactAdd from './ContactAdd';
import CustomModal from '../../../common/CustomModal';

// FORMATTED MESSAGES
import AddressBookFormattedMessages from '../../../../messages/websms/AddressBookFormattedMessages';

// NETWORKING
import useAddressBookRequest from '../../../../requests/AddressBookRequest';

// CONFIG
import config from '../../../../Config';
import Paginator from '../../../common/Paginator';
import Loader from '../../../common/Loader';

const ContactList = (props) => {
  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageLength, setPageLength] = useState(config.defaultPL ?? 10);
  const [contactSearch, setContactSearch] = useState({});
  const [addingContact, setAddContact] = useState(false);
  const [smsContacts, setSmsContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [deleteContactList, setDeleteContacts] = useState([]);
  const [allChecked, setAllChecked] = useState(false);
  const {
    getSmsContacts,
    exportContacts,
    deleteContact,
    deleteSelectedSmsContacts,
  } = useAddressBookRequest();

  const getData = useCallback(async () => {
    if (props.list) {
      setSmsContacts(props.list);
    } else {
      const smsContacts = await getSmsContacts(props.errorHandler);
      setSmsContacts(smsContacts);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    getData();
  }, [getData]);

  /*
    Handles the change event when clicking a pagination number
  */
  const changeEvent = (value) => {
    setPageNumber(value);
  };

  const changePageLength = (e) => {
    const value = e.target.value;
    setPageLength(value);
    setPageNumber(1);
  };

  /*
    Handles changes in the form
  */
  const handleContactSearchChange = async (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;
    let updatedContactSearch = {
      ...contactSearch,
      [name]: value,
    };
    if (name === 'submit') updatedContactSearch = contactSearch;
    if (name === 'searchContent') {
      setContactSearch(updatedContactSearch);
    } else {
      handleSubmit(updatedContactSearch);
    }
  };

  const handleKeyDown = async (event) => {
    const { key } = event;
    if (key === 'Enter') handleSubmit(contactSearch);
  };

  const handleSubmit = async (updatedSearchValue) => {
    setLoading(true);
    const data = await getSmsContacts(props.errorHandler, updatedSearchValue);
    setSmsContacts(data);
    setContactSearch(updatedSearchValue);
    setOpenModal(false);
    setModalType(null);
    setLoading(false);
    setPageNumber(1);
  };

  const handleExportContacts = async () => {
    await exportContacts({ entry_type: 'global' }, props.errorHandler);
  };

  const generateButtons = () => {
    return (
      <Stack sx={{ marginTop: 4, marginBottom: 1, gap: 1 }}>
        <Stack
          sx={{
            flexDirection: { lg: 'row' },
            justifyContent: 'space-between',
            alignItems: { lg: 'end' },
            gap: 2,
          }}
        >
          <Stack
            sx={{
              flexDirection: 'row',
              gap: 2,
              justifyContent: { xs: 'space-between', lg: 'start' },
            }}
          >
            <Button
              variant="contained"
              color="secondary"
              onClick={props.showContactImporter}
            >
              {AddressBookFormattedMessages.importContacts}
            </Button>

            <Button
              variant="contained"
              onClick={() => {
                setAddContact(true);
                setOpenModal(true);
                setModalType('add');
              }}
              color="secondary"
            >
              {AddressBookFormattedMessages.createNewButton}
            </Button>
          </Stack>

          <Stack
            sx={{
              flexDirection: 'row',
              gap: 2,
              justifyContent: { xs: 'space-between', lg: 'start' },
            }}
          >
            <Button
              color="secondary"
              variant="link"
              sx={{ paddingY: 0 }}
              onClick={handleExportContacts}
            >
              {AddressBookFormattedMessages.exportContacts}
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              disabled={deleteContactList.length === 0}
              onClick={() => {
                setOpenModal(true);
                setModalType('delete');
              }}
            >
              {AddressBookFormattedMessages.deleteSelected}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    );
  };

  const handleAllCheckedChange = () => {
    const currentDeleted = smsContacts
      .slice(
        (pageNumber - 1) * pageLength,
        (pageNumber - 1) * pageLength + pageLength
      )
      .map((item) => item.sms_contact_id);

    if (allChecked) {
      // Uncheck only the templates on the current page
      setDeleteContacts((prev) =>
        prev.filter((id) => !currentDeleted.includes(id))
      );
    } else {
      // Check only the templates on the current page, keeping existing selections
      setDeleteContacts((prev) => [...new Set([...prev, ...currentDeleted])]);
    }
    setAllChecked(!allChecked);
  };

  const selectRowsToBeDeleted = (id) => {
    setDeleteContacts((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const deleteSmsContact = async () => {
    await deleteContact(selectedContact.sms_contact_id, props.errorHandler);
    const data = await getSmsContacts(props.errorHandler);

    setSmsContacts(data);
    setSelectedContact(null);
    setOpenModal(false);
    setModalType(null);
    setLoading(false);
  };

  const handleDeleteSelectedSmsContacts = async () => {
    setLoading(true);
    await deleteSelectedSmsContacts(deleteContactList, props.errorHandler);

    setDeleteContacts([]);
    handleCloseModal();
    setLoading(false);
  };

  const handleCloseModal = async (refresh) => {
    if (refresh) {
      const data = await getSmsContacts(props.errorHandler);
      setSmsContacts(data);
    }
    setAddContact(null);
    setSelectedContact(null);
    setOpenModal(false);
    setModalType(null);
    setLoading(false);
  };

  /*
    Generate the table for Contacts
  */
  const generateTableRows = (displayList) => {
    return displayList.map((smsContact, i) => {
      return (
        <ListItem
          key={smsContact.sms_contact_id}
          id={smsContact.sms_contact_id}
          smsContact={smsContact}
          onClick={() => {
            setSelectedContact(smsContact);
            setOpenModal(true);
            setModalType('selected');
          }}
          selectRowsToBeDeleted={selectRowsToBeDeleted}
          accessLevel={props.accessLevel}
          accessType={props.accessType}
          deleteContactList={deleteContactList}
        />
      );
    });
  };

  const generateTable = () => {
    if (loading) return <Loader sx={{ marginTop: 2 }} />;
    (!smsContacts || !smsContacts.length) && (
      <Typography>No address book contacts found</Typography>
    );
    const displayList = smsContacts.slice(
      (pageNumber - 1) * pageLength,
      (pageNumber - 1) * pageLength + pageLength
    );

    return (
      <Box>
        <TableContainer
          sx={{ marginBottom: 4, overflowX: 'auto', width: '100%' }}
        >
          <Table variant="rounded">
            <TableHead>
              <TableRow key="head">
                <TableCell>{AddressBookFormattedMessages.nameText}</TableCell>
                <TableCell>{AddressBookFormattedMessages.emailText}</TableCell>
                <TableCell>{AddressBookFormattedMessages.mobileText}</TableCell>
                <TableCell style={{ width: '10%' }}>
                  {AddressBookFormattedMessages.type}
                </TableCell>
                <TableCell>{AddressBookFormattedMessages.lists}</TableCell>
                <TableCell align="right">
                  <Checkbox
                    name="delete"
                    disabled={
                      props.accessLevel > 0 && props.accessType > 0
                        ? false
                        : true
                    }
                    onChange={() => handleAllCheckedChange()}
                    sx={{ padding: 0 }}
                    checked={allChecked}
                  />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{generateTableRows(displayList)}</TableBody>
          </Table>
        </TableContainer>

        <Paginator
          itemCount={smsContacts.length}
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          pageLength={pageLength}
          changeEvent={changeEvent}
          editFunction={(e) => changePageLength(e)}
          showTotal={'Found'}
          toolTip={'Maximum of 4,000 records shown'}
        />
      </Box>
    );
  };

  return (
    <React.Fragment>
      <Box
        sx={{
          backgroundColor: 'common.white',
          borderRadius: '0 0 20px 20px',
          paddingX: 4,
          paddingY: 2,
          maxWidth: 800,
        }}
      >
        <Stack
          sx={{
            flexDirection: { lg: 'row' },
            justifyContent: 'space-between',
            alignItems: { lg: 'center' },
            gap: 2,
          }}
        >
          <Stack sx={{ flex: 1 }}>
            <InputLabel>
              {AddressBookFormattedMessages.searchContact}
            </InputLabel>

            <TextField
              id="contactSearch"
              autoFocus
              type="text"
              name="searchContent"
              onKeyDown={handleKeyDown}
              onChange={handleContactSearchChange}
              variant="outlined"
              fullWidth
              size="small"
            />
          </Stack>

          <Stack sx={{ gap: 1 }}>
            Filter
            <Stack sx={{ flexDirection: 'row', gap: 2, paddingLeft: 1.1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="personal"
                    value="personal"
                    color="secondary"
                    onChange={handleContactSearchChange}
                    id="P"
                    sx={{ padding: 0 }}
                  />
                }
                label={AddressBookFormattedMessages.personal}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="global"
                    value="global"
                    onChange={handleContactSearchChange}
                    id="G"
                    color="secondary"
                    sx={{ padding: 0 }}
                  />
                }
                label={AddressBookFormattedMessages.global}
              />
            </Stack>
          </Stack>

          <Box>
            <Button
              variant="outlined"
              color="secondary"
              name="submit"
              onClick={handleContactSearchChange}
            >
              Search
            </Button>
          </Box>
        </Stack>
      </Box>
      {generateButtons()}
      {generateTable()}

      <CustomModal
        open={openModal}
        setOpen={setOpenModal}
        title={
          modalType == 'selected'
            ? 'Edit Contacts'
            : modalType == 'delete'
            ? 'Delete Contacts'
            : 'New Contact'
        }
        width={500}
        icon="contact"
      >
        {modalType == 'add' && (
          <ContactAdd
            callBack={handleCloseModal}
            accessLevel={props.accessLevel}
            accessType={props.accessType}
            errorHandler={props.errorHandler}
          />
        )}

        {modalType == 'selected' && (
          <ContactEdit
            callBack={handleCloseModal}
            selected={selectedContact}
            delete={deleteSmsContact}
            errorHandler={props.errorHandler}
            accessLevel={props.accessLevel}
            accessType={props.accessType}
          />
        )}

        {modalType == 'delete' && (
          <Box>
            <Typography gutterBottom>
              {`${deleteContactList.length}
              ${
                deleteContactList.length === 1 ? 'Contact' : 'Contacts '
              } Selected`}
            </Typography>

            <Typography gutterBottom>
              {AddressBookFormattedMessages.deleteContactsBody}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                justifyContent: 'end',
              }}
            >
              <Button
                onClick={() => handleCloseModal(false)}
                variant="link"
                color="secondary"
              >
                {AddressBookFormattedMessages.cancelText}
              </Button>

              <Button
                onClick={handleDeleteSelectedSmsContacts}
                variant="contained"
                color="secondary"
              >
                {AddressBookFormattedMessages.deleteText}
              </Button>
            </Box>
          </Box>
        )}
      </CustomModal>
    </React.Fragment>
  );
};

export default ContactList;
